import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  IconButton,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import SettingsIcon from "@mui/icons-material/Settings";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import FolderOffIcon from "@mui/icons-material/FolderOff";
import InstanceLeftSidebar from "../../InstanceLeftSidebar";
import { exportSupplierCsv, fetchSupplierReport } from "../../../../services/reports";
import { useParams } from "react-router-dom";
import { UITableShell, UITableToolbar } from "../../../../components/ui";
import ReportSavedReportsButton from "../shared/ReportSavedReportsButton";
import ReportColumnVisibilityButton from "../shared/ReportColumnVisibilityButton";
import { normalizeHiddenColumns as normalizeSavedHiddenColumns } from "../shared/savedReportUtils";
import {
  reportContentSx,
  reportFilterCardSx,
  reportFilterGridSx,
  reportPageWrapperSx,
  reportShellSx,
  reportSummaryCardSx,
  reportTableBodySx,
  reportTableContainerSx,
  reportTableFooterSx,
  reportTableHeaderCellSx,
  reportTablePaperSx,
  reportTableSectionSx,
} from "../shared/reportLayout";

const currency = (n) => `CA$${(Number(n) || 0).toFixed(2)}`;

const densitySettings = {
  compact: { py: 0.75, fontSize: "0.8125rem" },
  comfortable: { py: 1.4, fontSize: "0.875rem" },
  spacious: { py: 2.05, fontSize: "0.9375rem" },
};

const ordinal = (day) => {
  const mod10 = day % 10;
  const mod100 = day % 100;
  if (mod10 === 1 && mod100 !== 11) return "st";
  if (mod10 === 2 && mod100 !== 12) return "nd";
  if (mod10 === 3 && mod100 !== 13) return "rd";
  return "th";
};

const friendlyDate = (value) => {
  if (!value) return "\u2014";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "\u2014";
  const month = date.toLocaleString("en-US", { month: "long", timeZone: "UTC" });
  const day = date.getUTCDate();
  const year = date.getUTCFullYear();
  return `${month} ${day}${ordinal(day)}, ${year}`;
};

const DEFAULT_SUMMARY = {
  totalSpend: 0,
  totalPoAmount: 0,
  totalAmountDue: 0,
  purchaseOrderCount: 0,
  supplierCount: 0,
};

const DEFAULT_FILTERS = {
  dateFrom: "",
  dateTo: "",
  supplier: "",
  poStatus: "",
  paymentStatus: "",
  q: "",
};

const getColumnValue = (row, field) => {
  if (!row || !field) return undefined;
  return field.split(".").reduce((value, key) => value?.[key], row);
};

const compareValues = (a, b, sortType = "string") => {
  if (sortType === "date") {
    const left = a ? new Date(a).getTime() : 0;
    const right = b ? new Date(b).getTime() : 0;
    return (Number.isFinite(left) ? left : 0) - (Number.isFinite(right) ? right : 0);
  }
  if (sortType === "currency" || sortType === "number") {
    return (Number(a) || 0) - (Number(b) || 0);
  }
  return String(a ?? "").localeCompare(String(b ?? ""), undefined, {
    numeric: true,
    sensitivity: "base",
  });
};

const getColumnKey = (column = {}) => String(column.field || column.headerName || "").trim();

const ReportTableContent = ({
  rows = [],
  columns = [],
  loading = false,
  emptyMessage = "No supplier data yet.",
  page = 0,
  rowsPerPage = 25,
  sortBy = "",
  sortDir = "asc",
  onSort,
  onPageChange,
  onRowsPerPageChange,
  density = "comfortable",
}) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  const safeColumns = Array.isArray(columns) ? columns : [];
  const cellDensity = densitySettings[density] || densitySettings.comfortable;
  const visibleColumnCount = Math.max(safeColumns.length, 1);
  const pageRows = safeRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <>
      <TableContainer
        sx={{
          ...reportTableContainerSx,
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          overflowX: "visible",
        }}
      >
        <Table stickyHeader size="small" sx={{ minWidth: 980 }}>
          <TableHead>
            <TableRow>
              {safeColumns.length > 0 ? safeColumns.map((column) => {
                const active = sortBy === column.field;
                return (
                  <TableCell
                    key={getColumnKey(column)}
                    onClick={() => onSort(column.field)}
                    sx={reportTableHeaderCellSx({
                      active,
                      sortable: true,
                      minWidth: column.minWidth || 120,
                    })}
                  >
                    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                      <Box component="span">{column.headerName || column.field}</Box>
                      {active ? (
                        sortDir === "asc"
                          ? <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                          : <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                      ) : null}
                    </Box>
                  </TableCell>
                );
              }) : (
                <TableCell sx={{ fontWeight: 700, bgcolor: "#f8fafc !important" }}>
                  Columns
                </TableCell>
              )}
            </TableRow>
          </TableHead>

          <TableBody sx={reportTableBodySx}>
            {loading ? Array.from({ length: Math.min(rowsPerPage, 10) }).map((_, rowIndex) => (
              <TableRow key={`skeleton-${rowIndex}`}>
                {Array.from({ length: visibleColumnCount }).map((__, cellIndex) => (
                  <TableCell key={`skeleton-${rowIndex}-${cellIndex}`} sx={{ ...cellDensity, whiteSpace: "nowrap" }}>
                    <Skeleton variant="text" width={cellIndex === 0 ? 160 : 100} />
                  </TableCell>
                ))}
              </TableRow>
            )) : null}

            {!loading && safeColumns.length === 0 ? (
              <TableRow>
                <TableCell colSpan={1} sx={{ py: 8, textAlign: "center" }}>
                  <FolderOffIcon sx={{ fontSize: 46, color: "text.disabled", mb: 1 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    No columns visible
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Use the toolbar column menu to show at least one column.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : null}

            {!loading && safeColumns.length > 0 && pageRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={visibleColumnCount} sx={{ py: 8, textAlign: "center" }}>
                  <FolderOffIcon sx={{ fontSize: 46, color: "text.disabled", mb: 1 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {emptyMessage}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Try adjusting filters or refreshing the report.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : null}

            {!loading && safeColumns.length > 0 ? pageRows.map((row, rowIndex) => (
              <TableRow
                key={row?.id || row?._id || row?.poNumber || row?.invoice || `${page}-${rowIndex}`}
                hover
                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
              >
                {safeColumns.map((column) => {
                  const value = getColumnValue(row, column.field);
                  const renderedValue = typeof column.renderCell === "function"
                    ? column.renderCell({ value, row })
                    : (value ?? "\u2014");

                  return (
                    <TableCell key={getColumnKey(column)} sx={{ ...cellDensity, whiteSpace: "nowrap", verticalAlign: "top" }}>
                      {renderedValue}
                    </TableCell>
                  );
                })}
              </TableRow>
            )) : null}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={reportTableFooterSx}>
        <TablePagination
          component="div"
          count={safeRows.length}
          page={page}
          onPageChange={(_, nextPage) => onPageChange(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => onRowsPerPageChange(Number(event.target.value))}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{
            border: 0,
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
              fontSize: "0.8125rem",
              color: "text.disabled",
              mb: 0,
            },
          }}
        />
      </Box>
    </>
  );
};

const SupplierReport = () => {
  const { instanceUniqueId } = useParams();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(DEFAULT_SUMMARY);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortBy, setSortBy] = useState("");
  const [sortDir, setSortDir] = useState("asc");
  const reportRequestSeq = React.useRef(0);
  const skipNextAutoLoadRef = React.useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem("eliteCore:reports:supplier:columns");
      const parsed = raw ? JSON.parse(raw) : [];
      setHiddenColumns(Array.isArray(parsed) ? parsed.map(String) : []);
    } catch {
      setHiddenColumns([]);
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("eliteCore:reports:supplier:columns", JSON.stringify(hiddenColumns));
  }, [hiddenColumns]);

  const load = useCallback(async (filtersOverride = null) => {
    const requestId = ++reportRequestSeq.current;
    const effectiveFilters = filtersOverride && typeof filtersOverride === "object"
      ? filtersOverride
      : filters;
    setLoading(true);

    try {
      const res = await fetchSupplierReport({ instanceUniqueId, ...effectiveFilters });
      if (requestId !== reportRequestSeq.current) return;
      setRows(Array.isArray(res?.rows) ? res.rows : []);
      setSummary(
        res?.summary && typeof res.summary === "object"
          ? { ...DEFAULT_SUMMARY, ...res.summary }
          : DEFAULT_SUMMARY,
      );
    } catch (error) {
      if (requestId !== reportRequestSeq.current) return;
      console.error("Failed to load supplier report", error);
      setRows([]);
      setSummary(DEFAULT_SUMMARY);
    } finally {
      if (requestId === reportRequestSeq.current) setLoading(false);
    }
  }, [filters, instanceUniqueId]);

  useEffect(() => {
    if (skipNextAutoLoadRef.current) {
      skipNextAutoLoadRef.current = false;
      return;
    }
    load();
  }, [load]);

  useEffect(() => {
    setPage(0);
  }, [filters, rows]);

  const columns = useMemo(
    () => ([
      { field: "supplierName", headerName: "Supplier", minWidth: 180, sortType: "string" },
      { field: "billDate", headerName: "Bill Date", minWidth: 150, sortType: "date", renderCell: (params) => friendlyDate(params.value) },
      { field: "orderId", headerName: "Order #", minWidth: 110, sortType: "string" },
      { field: "poNumber", headerName: "PO #", minWidth: 110, sortType: "string" },
      { field: "poStatus", headerName: "PO Status", minWidth: 130, sortType: "string" },
      { field: "received", headerName: "Received", minWidth: 120, sortType: "string" },
      { field: "poAmount", headerName: "PO Amount", minWidth: 130, sortType: "currency", renderCell: (params) => currency(params.value) },
      { field: "amount", headerName: "Spend", minWidth: 120, sortType: "currency", renderCell: (params) => currency(params.value) },
      { field: "amountDue", headerName: "Amount Due", minWidth: 140, sortType: "currency", renderCell: (params) => currency(params.value) },
      { field: "invoice", headerName: "Invoice", minWidth: 130, sortType: "string" },
    ]),
    [],
  );

  useEffect(() => {
    const validColumnKeys = new Set(columns.map((column) => getColumnKey(column)).filter(Boolean));
    setHiddenColumns((previous) => {
      const next = (Array.isArray(previous) ? previous : []).filter((key) => validColumnKeys.has(String(key)));
      return next.length >= validColumnKeys.size ? [] : next;
    });
  }, [columns]);

  const visibleColumns = useMemo(() => {
    const hiddenSet = new Set((Array.isArray(hiddenColumns) ? hiddenColumns : []).map(String));
    const nextColumns = columns.filter((column) => !hiddenSet.has(getColumnKey(column)));
    return nextColumns.length ? nextColumns : columns;
  }, [columns, hiddenColumns]);

  const sortedRows = useMemo(() => {
    const safeRows = Array.isArray(rows) ? rows : [];
    if (!sortBy) return safeRows;
    const column = columns.find((item) => item.field === sortBy);
    const dir = sortDir === "desc" ? -1 : 1;
    return [...safeRows].sort(
      (left, right) => compareValues(
        getColumnValue(left, sortBy),
        getColumnValue(right, sortBy),
        column?.sortType,
      ) * dir,
    );
  }, [columns, rows, sortBy, sortDir]);

  const summaryCards = useMemo(
    () => [
      ["Total Spend", currency(summary.totalSpend)],
      ["Total PO Amount", currency(summary.totalPoAmount)],
      ["Total Amount Due", currency(summary.totalAmountDue)],
      ["PO Count", summary.purchaseOrderCount ?? 0],
      ["Supplier Count", summary.supplierCount ?? 0],
    ],
    [summary],
  );

  const handleSort = (columnKey) => {
    if (sortBy === columnKey) {
      if (sortDir === "asc") {
        setSortDir("desc");
      } else {
        setSortBy("");
        setSortDir("asc");
      }
      return;
    }

    setSortBy(columnKey);
    setSortDir("asc");
  };

  const onExport = async () => {
    const response = await exportSupplierCsv({ instanceUniqueId, ...filters });
    const url = window.URL.createObjectURL(
      new Blob([response.data], { type: "text/csv;charset=utf-8;" }),
    );
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "supplier-report.csv");
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleReset = () => {
    setFilters(DEFAULT_FILTERS);
  };

  const currentSavedReportState = useMemo(
    () => ({
      version: 1,
      filters: {
        dateFrom: filters.dateFrom || "",
        dateTo: filters.dateTo || "",
        supplier: filters.supplier || "",
        poStatus: filters.poStatus || "",
        paymentStatus: filters.paymentStatus || "",
        q: filters.q || "",
      },
      hiddenColumns: Array.isArray(hiddenColumns) ? hiddenColumns.filter(Boolean) : [],
      sort: {
        field: sortBy,
        direction: sortDir,
      },
    }),
    [filters, hiddenColumns, sortBy, sortDir],
  );

  const applySavedReportState = useCallback(async (savedState = {}) => {
    const savedFilters = savedState?.filters && typeof savedState.filters === "object"
      ? savedState.filters
      : {};
    const validColumnKeys = new Set(columns.map((column) => getColumnKey(column)).filter(Boolean));

    const nextHiddenColumns = normalizeSavedHiddenColumns({
      savedState,
      validColumnKeys: Array.from(validColumnKeys),
      fallbackHiddenColumns: [],
    });

    const nextFilters = {
      ...DEFAULT_FILTERS,
      ...savedFilters,
      dateFrom: String(savedFilters?.dateFrom || ""),
      dateTo: String(savedFilters?.dateTo || ""),
      supplier: String(savedFilters?.supplier || ""),
      poStatus: String(savedFilters?.poStatus || ""),
      paymentStatus: String(savedFilters?.paymentStatus || ""),
      q: String(savedFilters?.q || ""),
    };

    setHiddenColumns(nextHiddenColumns);
    setSortBy(columns.some((column) => column.field === savedState?.sort?.field) ? savedState.sort.field : "");
    setSortDir(savedState?.sort?.direction === "desc" ? "desc" : "asc");
    skipNextAutoLoadRef.current = true;
    setFilters(nextFilters);
    await load(nextFilters);
  }, [columns, load]);

  return (
    <div className="instance-dashboard instance-supplier-report-page">
      <InstanceLeftSidebar />

      <div className="rightSide">
        <Box className="fade-in-effect-usability" sx={reportPageWrapperSx}>
          <UITableShell customLayout paperSx={reportShellSx}>
            <UITableToolbar sx={{ backgroundColor: "background.paper", flexShrink: 0 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={1.5}
                sx={{ width: "100%" }}
              >
                <Stack direction="row" alignItems="center" spacing={1.25} flexWrap="wrap" useFlexGap>
                  <Typography variant="h6" fontWeight={700} sx={{ color: "text.primary", lineHeight: 1 }}>
                    Supplier Report
                  </Typography>
                  <Chip
                    label={loading ? "Loading..." : `${Array.isArray(rows) ? rows.length : 0} total`}
                    size="small"
                    sx={{
                      backgroundColor: "var(--ec-status-neutral-bg)",
                      color: "var(--ec-status-neutral-text)",
                      border: "1px solid var(--ec-status-neutral-border)",
                      fontWeight: 600,
                      fontSize: "0.72rem",
                      height: 22,
                    }}
                  />
                  <Chip
                    label="supplier"
                    size="small"
                    sx={{
                      backgroundColor: "var(--ec-status-info-bg)",
                      color: "var(--ec-status-info-text)",
                      border: "1px solid var(--ec-status-info-border)",
                      fontWeight: 600,
                      fontSize: "0.72rem",
                      height: 22,
                      textTransform: "none",
                    }}
                  />
                </Stack>

                <Stack direction="row" spacing={0.5} alignItems="center">
                  <ReportSavedReportsButton
                    instanceUniqueId={instanceUniqueId}
                    reportKey="supplier"
                    currentState={currentSavedReportState}
                    onApplyReport={applySavedReportState}
                  />
                  <ReportColumnVisibilityButton
                    columns={columns}
                    hiddenColumns={hiddenColumns}
                    onHiddenColumnsChange={setHiddenColumns}
                  />
                  <Tooltip title="Row density">
                    <span>
                      <IconButton size="small" disabled sx={{ color: "text.secondary" }}>
                        <SettingsIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 24, alignSelf: "center" }} />
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    disableElevation
                    startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
                    onClick={onExport}
                    sx={{
                      fontWeight: 700,
                      textTransform: "none",
                      height: 34,
                      px: 2,
                      boxShadow: "none",
                      "&:hover": { boxShadow: "none" },
                    }}
                  >
                    Export CSV
                  </Button>
                </Stack>
              </Stack>
            </UITableToolbar>

            <Box sx={{ ...reportContentSx, minWidth: 0, maxWidth: "100%" }}>
              <Paper elevation={0} sx={reportFilterCardSx}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 2,
                    mb: 1.5,
                    flexWrap: "wrap",
                  }}
                >
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                      Supplier Report Controls
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Analyze supplier activity, purchasing volume, costs, products, projects, and associated representatives.
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button variant="outlined" size="small" onClick={load} disabled={loading}>
                      Get Report
                    </Button>
                    <Button variant="outlined" size="small" onClick={handleReset}>
                      Reset
                    </Button>
                  </Stack>
                </Box>

                <Box sx={reportFilterGridSx}>
                  <TextField
                    label="Date From"
                    type="date"
                    value={filters.dateFrom}
                    onChange={(event) => setFilters((previous) => ({ ...previous, dateFrom: event.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                  <TextField
                    label="Date To"
                    type="date"
                    value={filters.dateTo}
                    onChange={(event) => setFilters((previous) => ({ ...previous, dateTo: event.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                  <TextField
                    label="Supplier"
                    value={filters.supplier}
                    onChange={(event) => setFilters((previous) => ({ ...previous, supplier: event.target.value }))}
                    size="small"
                  />
                  <TextField
                    select
                    label="PO Status"
                    value={filters.poStatus}
                    onChange={(event) => setFilters((previous) => ({ ...previous, poStatus: event.target.value }))}
                    SelectProps={{ native: true }}
                    size="small"
                  >
                    <option value="">All</option>
                    <option value="Created">Created</option>
                    <option value="Submitted">Submitted</option>
                    <option value="Billed">Billed</option>
                    <option value="Closed">Closed</option>
                  </TextField>
                  <TextField
                    select
                    label="Received"
                    value={filters.paymentStatus}
                    onChange={(event) => setFilters((previous) => ({ ...previous, paymentStatus: event.target.value }))}
                    SelectProps={{ native: true }}
                    size="small"
                  >
                    <option value="">All</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </TextField>
                  <TextField
                    label="Search"
                    value={filters.q}
                    onChange={(event) => setFilters((previous) => ({ ...previous, q: event.target.value }))}
                    size="small"
                  />
                </Box>
              </Paper>

              <Box
                sx={{
                  display: "grid",
                  gap: 1.5,
                  gridTemplateColumns: {
                    xs: "repeat(2, minmax(0, 1fr))",
                    md: "repeat(3, minmax(0, 1fr))",
                    xl: "repeat(5, minmax(0, 1fr))",
                  },
                }}
              >
                {summaryCards.map(([label, value]) => (
                  <Paper key={label} elevation={0} sx={reportSummaryCardSx}>
                    <Typography variant="caption" color="text.secondary">
                      {label}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {value}
                    </Typography>
                  </Paper>
                ))}
              </Box>

              <Box sx={{ ...reportTableSectionSx, minWidth: 0, maxWidth: "100%" }}>
                <Paper
                  elevation={0}
                  sx={{ ...reportTablePaperSx, minWidth: 0, maxWidth: "100%", overflow: "hidden" }}
                >
                  <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0, overflow: "hidden" }}>
                    <Box
                      sx={{
                        width: "100%",
                        maxWidth: "100%",
                        minWidth: 0,
                        overflowX: "auto",
                        overflowY: "hidden",
                      }}
                    >
                      <ReportTableContent
                        rows={sortedRows}
                        columns={visibleColumns}
                        loading={loading}
                        emptyMessage="No supplier records found for the selected filters."
                        page={page}
                        rowsPerPage={rowsPerPage}
                        sortBy={sortBy}
                        sortDir={sortDir}
                        onSort={handleSort}
                        onPageChange={setPage}
                        onRowsPerPageChange={(value) => {
                          setRowsPerPage(value);
                          setPage(0);
                        }}
                        density="comfortable"
                      />
                    </Box>
                  </Box>
                </Paper>
              </Box>
            </Box>
          </UITableShell>
        </Box>
      </div>
    </div>
  );
};

export default SupplierReport;
